import { logger } from '@fe/core';
import { EventBus } from '../../app/contexts/event-bus';
import { addNotification, dismissNotification, NotificationType, transformNotification } from '../../app/state/slices/global-slice';
import { store } from '../../app/state/store';
import { getWorker } from '../../client/worker-container';
import { FPMMessageType } from '../../common/enums';
import type { AnalyzerRoute } from '../../components/route-analyzer/types';
import { FPMInfoMessage, FPMMessage } from '../../messages/message-parser';
import { CalculateRouteRequestData, MessageType, RouteRequestData, RouteRequestType, VerifyRouteRequestData } from '../actor-messages';

// Tracks requests sent to the FPM / desktop integration and gives the user feedback (toasts) about their progress.

export enum FpmRequestKind {
    routes = 'routes',
    calculate = 'calculate',
    verify = 'verify',
    goToFlight = 'goToFlight'
}

export type FpmResultSeverity = 'success' | 'info' | 'warning' | 'error';

export interface FpmRequestFinished {
    kind: FpmRequestKind;
    severity: FpmResultSeverity;
    message: string;
}

interface PendingRequest {
    kind: FpmRequestKind;
    // e.g. "routes for ABC123", used in follow-up messages
    description: string;
    notificationId: string | null;
    timeoutId: number | null;
}

const RESPONSE_TIMEOUT_S: Record<FpmRequestKind, number> = {
    [FpmRequestKind.routes]: 30,
    [FpmRequestKind.calculate]: 60,
    [FpmRequestKind.verify]: 60,
    [FpmRequestKind.goToFlight]: 30
};
// Calculate / verify can be answered asynchronously by the customer system (Pending -> VerifyResult / CalculateResult)
const ASYNC_RESULT_TIMEOUT_S = 15 * 60;
// Go to flight has no success response, only errors. Assume success if no error arrived by then (but keep listening for errors).
const GO_TO_FLIGHT_ASSUME_SENT_S = 3;

const TEXT_INFO_SEVERITY: Record<number, FpmResultSeverity> = {
    0: 'success', // INFO_SUCCESS
    1: 'error', // INFO_ERROR
    2: 'warning', // INFO_WARN
    3: 'info' // INFO_INFO
};

const SEVERITY_NOTIFICATION: Record<FpmResultSeverity, NotificationType> = {
    success: NotificationType.generalSuccess,
    info: NotificationType.generalInfo,
    warning: NotificationType.generalWarning,
    error: NotificationType.generalError
};

function kindFromSubCode(subCode: number): FpmRequestKind | null {
    switch (subCode) {
        case FPMMessageType.GoToFlight:
        case FPMMessageType.GoToFlightRs:
            return FpmRequestKind.goToFlight;
        case FPMMessageType.RequestRoute:
        case FPMMessageType.RouteResponse:
            return FpmRequestKind.routes;
        case FPMMessageType.CalculateRouteRq:
        case FPMMessageType.CalculateRouteNotif:
        case FPMMessageType.CalculateRouteRs:
        case FPMMessageType.PlannedRouteResponse:
            return FpmRequestKind.calculate;
        case FPMMessageType.VerifyRouteRequest:
        case FPMMessageType.VerifyRouteResponse:
            return FpmRequestKind.verify;
        default:
            return null;
    }
}

export function getIntegrationName(): string {
    const state = store.getState();
    if (state.user.rights.FPMIntegration) {
        return 'FPM';
    }
    if (state.user.rights.DesktopIntegration) {
        return state.global.integrationState.desktopAppName;
    }
    return 'integration';
}

function plural(count: number, singular: string) {
    return `${count} ${count === 1 ? singular : singular + 's'}`;
}

function defaultText(severity: FpmResultSeverity) {
    switch (severity) {
        case 'success':
            return 'Done';
        case 'error':
            return 'Request failed';
        case 'warning':
            return 'Request completed with warnings';
        default:
            return 'No result';
    }
}

class FpmRequestTracker {
    private readonly pending = new Map<FpmRequestKind, PendingRequest>();

    start(kind: FpmRequestKind, description: string, progressMessage: string) {
        const previous = this.pending.get(kind);
        if (previous) {
            this.clearTimer(previous);
            if (previous.notificationId) {
                store.dispatch(dismissNotification({ id: previous.notificationId }));
            }
        }

        const request: PendingRequest = {
            kind,
            description,
            notificationId: null,
            timeoutId: null
        };
        this.pending.set(kind, request);
        this.show(request, NotificationType.integrationInProgress, progressMessage, false);

        if (kind === FpmRequestKind.goToFlight) {
            this.setTimer(request, GO_TO_FLIGHT_ASSUME_SENT_S, () => {
                this.show(request, NotificationType.generalSuccess, `Sent ${description} to ${getIntegrationName()}`, true);
                // keep listening for a late error, then forget the request silently
                this.setTimer(request, RESPONSE_TIMEOUT_S[kind] - GO_TO_FLIGHT_ASSUME_SENT_S, () => this.pending.delete(kind));
            });
        } else {
            this.setTimer(request, RESPONSE_TIMEOUT_S[kind], () =>
                this.finish(kind, 'warning', `No response from ${getIntegrationName()} for ${description}. Please try again later.`)
            );
        }
    }

    // The request could not be sent at all
    fail(kind: FpmRequestKind, message: string) {
        this.finish(kind, 'error', message);
    }

    handleMessage(message: FPMMessage) {
        switch (message.cSubCode) {
            case FPMMessageType.TextInfo:
                this.handleTextInfo(message);
                break;
            case FPMMessageType.GoToFlightRs: {
                const request = this.pending.get(FpmRequestKind.goToFlight);
                if (request) {
                    this.finish(FpmRequestKind.goToFlight, 'success', `Opened ${request.description} in ${getIntegrationName()}`);
                }
                break;
            }
            case FPMMessageType.CalculateRouteNotif: {
                const request = this.pending.get(FpmRequestKind.calculate);
                if (request) {
                    this.waitForAsyncResult(
                        request,
                        `${getIntegrationName()} accepted the request, waiting for the ${request.description}…`
                    );
                }
                break;
            }
        }
    }

    // Called once routes received from the integration were added to the route analyzer
    routesReceived(kind: FpmRequestKind.routes | FpmRequestKind.calculate | FpmRequestKind.verify, count: number) {
        const app = getIntegrationName();
        const request = this.pending.get(kind);

        let message: string;
        if (kind === FpmRequestKind.verify) {
            message = `Verified route from ${app} added to Route Analyzer`;
        } else if (kind === FpmRequestKind.calculate) {
            message = `${count === 1 ? 'Calculated route' : plural(count, 'calculated route')} from ${app} added to Route Analyzer`;
        } else if (request) {
            message = `${plural(count, 'route')} from ${app} added to Route Analyzer`;
        } else {
            // customer initiated (route push)
            message = `${app} sent ${plural(count, 'route')}, added to Route Analyzer`;
        }

        this.finish(kind, 'success', message, NotificationType.integrationRoutesReceived);
    }

    // A response arrived but contained nothing usable
    emptyResult(kind: FpmRequestKind, message: string) {
        this.finish(kind, 'warning', message);
    }

    private handleTextInfo(message: FPMInfoMessage) {
        if (message.cMsgSrc === FPMMessageType.FMPUserRs) {
            // login / rights problem, not related to a user action
            this.notify('warning', `${getIntegrationName()} integration is not available${message.pcText ? `: ${message.pcText}` : ''}`);
            return;
        }

        const severity = TEXT_INFO_SEVERITY[message.cMsgType] ?? 'info';
        let kind = kindFromSubCode(message.cMsgSrc);
        if (!kind && this.pending.size === 1) {
            kind = this.pending.keys().next().value ?? null;
        }
        
        const text = `${getIntegrationName()}: ${severity !== 'error' ? message.pcText || defaultText(severity) : defaultText(severity)}`;
        const request = kind ? this.pending.get(kind) : undefined;

        if (!kind || !request) {
            this.notify(severity, text);
            return;
        }

        // The customer system can answer Pending first and deliver the result later
        if (
            (kind === FpmRequestKind.calculate || kind === FpmRequestKind.verify) &&
            severity === 'info' &&
            /pending/i.test(message.pcText)
        ) {
            this.waitForAsyncResult(request, `${text}. The result will be added to Route Analyzer when ready…`);
            return;
        }

        this.finish(kind, severity, text);
    }

    private waitForAsyncResult(request: PendingRequest, message: string) {
        this.show(request, NotificationType.integrationInProgress, message, false);
        this.setTimer(request, ASYNC_RESULT_TIMEOUT_S, () =>
            this.finish(
                request.kind,
                'info',
                `${getIntegrationName()} has not returned the ${request.description} yet. It will be added to Route Analyzer if it arrives later.`
            )
        );
    }

    private finish(kind: FpmRequestKind, severity: FpmResultSeverity, message: string, type = SEVERITY_NOTIFICATION[severity]) {
        const request = this.pending.get(kind);
        if (request) {
            this.clearTimer(request);
            this.pending.delete(kind);
            this.show(request, type, message, true);
        } else {
            store.dispatch(addNotification({ type, customMessage: message }));
        }
        EventBus.fire('fpm-request-finished', { kind, severity, message });
    }

    private notify(severity: FpmResultSeverity, message: string) {
        store.dispatch(addNotification({ type: SEVERITY_NOTIFICATION[severity], customMessage: message }));
    }

    private show(request: PendingRequest, type: NotificationType, message: string, final: boolean) {
        // null = keep until replaced, undefined = default auto dismiss
        const disposeAfterSeconds = final ? undefined : null;
        const notificationId = request.notificationId;
        const exists = notificationId !== null && store.getState().global.notifications.some((x) => x.id === notificationId);

        if (exists) {
            store.dispatch(transformNotification({ id: notificationId, type, customMessage: message, disposeAfterSeconds }));
        } else {
            const action = store.dispatch(addNotification({ type, customMessage: message, disposeAfterSeconds }));
            request.notificationId = action.payload.id;
        }
    }

    private setTimer(request: PendingRequest, seconds: number, callback: () => void) {
        this.clearTimer(request);
        request.timeoutId = self.setTimeout(() => {
            request.timeoutId = null;
            // ignore if the request was superseded meanwhile
            if (this.pending.get(request.kind) === request) {
                callback();
            }
        }, seconds * 1000);
    }

    private clearTimer(request: PendingRequest) {
        if (request.timeoutId !== null) {
            self.clearTimeout(request.timeoutId);
            request.timeoutId = null;
        }
    }
}

export const fpmRequestTracker = new FpmRequestTracker();

async function send(kind: FpmRequestKind, sendRequest: () => Promise<boolean>, notSentMessage: string) {
    try {
        if (!(await sendRequest())) {
            fpmRequestTracker.fail(kind, notSentMessage);
        }
    } catch (err) {
        logger.trackException(err);
        fpmRequestTracker.fail(kind, `Could not send the request to ${getIntegrationName()}`);
    }
}

export function requestFpmRoutes(data: RouteRequestData) {
    const app = getIntegrationName();
    const subject = data.acid || `${data.origin ?? ''}-${data.destination ?? ''}`;
    const description = `${data.type === RouteRequestType.Variants ? 'route variants' : 'routes'} for ${subject}`;

    fpmRequestTracker.start(FpmRequestKind.routes, description, `Requesting ${description} from ${app}…`);
    return send(
        FpmRequestKind.routes,
        () => getWorker().actor.sendAsync({ type: MessageType.sendFPMRequestRoute, data }),
        `Could not request ${description} from ${app}: not connected`
    );
}

export function requestFpmCalculateRoute(data: CalculateRouteRequestData, routeLabel: string) {
    const app = getIntegrationName();

    fpmRequestTracker.start(FpmRequestKind.calculate, `calculation of ${routeLabel}`, `Sending ${routeLabel} to ${app} for calculation…`);
    return send(
        FpmRequestKind.calculate,
        () => getWorker().actor.sendAsync({ type: MessageType.sendFPMCalculateRoute, data }),
        `Could not send ${routeLabel} to ${app}: not connected`
    );
}

export function requestFpmVerifyRoute(data: VerifyRouteRequestData, routeLabel: string) {
    const app = getIntegrationName();

    fpmRequestTracker.start(FpmRequestKind.verify, `verification of ${routeLabel}`, `Sending ${routeLabel} to ${app} for verification…`);
    return send(
        FpmRequestKind.verify,
        () => getWorker().actor.sendAsync({ type: MessageType.sendFPMVerifyRoute, data }),
        `Could not send ${routeLabel} to ${app}: not connected`
    );
}

export function requestFpmGoToFlight(flightId: number, acid: string | null) {
    const app = getIntegrationName();
    const description = acid || 'flight';

    fpmRequestTracker.start(FpmRequestKind.goToFlight, description, `Opening ${description} in ${app}…`);
    return send(
        FpmRequestKind.goToFlight,
        () => getWorker().actor.sendAsync({ type: MessageType.sendFPMGoToFlight, data: flightId }),
        `Could not open ${description} in ${app}: flight not found or not connected`
    );
}

export type FpmRouteAction = 'calculate' | 'verify';

// Calculate or verify a route analyzer route. The route has to be linked to a flight (ACID / departure time).
export async function sendRouteToFpm(action: FpmRouteAction, route: Pick<AnalyzerRoute, 'flightId' | 'label' | 'origin' | 'route'>) {
    const flight =
        route.flightId !== undefined
            ? await getWorker().actor.sendAsync({ type: MessageType.getFpmFlightRequestData, data: route.flightId })
            : null;

    if (!flight) {
        notifyFpmRequestNotPossible(
            `Cannot ${action} ${route.label} in ${getIntegrationName()}: ${
                route.flightId === undefined ? 'the route is not linked to a flight' : 'the flight is no longer available'
            }`
        );
        return;
    }

    const data = {
        acid: flight.acid,
        origin: route.origin,
        edt: flight.edt,
        route: route.route
    };

    if (action === 'calculate') {
        await requestFpmCalculateRoute(data, route.label);
    } else {
        // TODO: verify whether cfmu / raim validation should be requested
        await requestFpmVerifyRoute({ ...data, cfmuValidation: false, raimValidation: false }, route.label);
    }
}

// For actions that can't be sent at all (missing data etc.)
export function notifyFpmRequestNotPossible(message: string) {
    store.dispatch(addNotification({ type: NotificationType.generalWarning, customMessage: message }));
}
